import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import usePageTitle from "@/lib/usePageTitle";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const Calendar = () => {
  usePageTitle("Calendar");
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => {
    api.get("/events").then((res) => setEvents(res.data)).catch(() => {});
  }, []);

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const startDay = first.getDay();
    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    }
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [cursor]);

  const eventsForDay = (day) => {
    if (!day) return [];
    const key = day.toISOString().slice(0, 10);
    return events.filter((e) => e.event_date === key);
  };

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const todayKey = new Date().toISOString().slice(0, 10);

  return (
    <div className="container-le section-pad" data-testid="calendar-page">
      <div className="max-w-2xl mb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-secondary mb-3">Township</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">Calendar</h1>
        <p className="text-muted-foreground mt-4">All scheduled meetings and events for Lake Eunice Township.</p>
      </div>

      <div className="flex items-center justify-between mb-5">
        <div className="font-heading text-2xl font-semibold">{monthLabel}</div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            data-testid="calendar-prev-btn"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            variant="outline"
            onClick={() => setCursor(new Date())}
            data-testid="calendar-today-btn"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            data-testid="calendar-next-btn"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-l border-t border-border bg-white rounded-xl overflow-hidden">
        {DAYS.map((d) => (
          <div key={d} className="border-r border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold py-2 px-3">
            {d}
          </div>
        ))}
        {grid.map((d, i) => {
          const key = d ? d.toISOString().slice(0, 10) : `empty-${i}`;
          const evs = eventsForDay(d);
          const isToday = d && key === todayKey;
          return (
            <div
              key={key}
              className={`min-h-[110px] border-r border-b border-border p-2 ${d ? "bg-white" : "bg-muted/20"}`}
              data-testid={d ? `day-${key}` : undefined}
            >
              {d && (
                <>
                  <div className={`text-sm font-medium mb-1 ${isToday ? "text-accent" : "text-foreground/80"}`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {evs.map((e) => (
                      <div
                        key={e.id}
                        className="text-[11px] leading-snug bg-primary text-primary-foreground rounded px-1.5 py-1"
                        title={`${e.title} — ${e.time} @ ${e.location}`}
                      >
                        {e.time && <span className="opacity-80 mr-1">{e.time}</span>}
                        {e.title}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
